/**
 * Location Page
 * Programmatic SEO city landing pages with dynamic content
 */

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs, { createBreadcrumbs } from '../components/Breadcrumbs';
import { RelatedPosts, ServiceLinks, ContextualCTA } from '../components/InternalLinks';
import Services from '../components/Services';
import Footer from '../components/Footer';
import BookAppointmentLink from '../components/BookAppointmentLink';
import OpensInNewTab from '../components/OpensInNewTab';
import { getLocationById, locations, faqItems } from '../data/seo';
import { 
  generateLocationSchema, 
  generateLocationMeta,
  generateFAQSchema,
  getRelatedLocations,
} from '../utils/seo';

const LocationPage: React.FC = () => {
  const { cityId } = useParams<{ cityId: string }>();
  const location = cityId ? getLocationById(cityId) : undefined;
  
  // 404 handling
  if (!location) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <div className="w-24 h-24 mx-auto mb-6 bg-brand-teal/10 rounded-full flex items-center justify-center">
            <i className="fas fa-map-marker-alt text-brand-teal text-4xl"></i>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Location Not Found</h1>
          <p className="text-gray-600 mb-8 max-w-md">
            We couldn't find the location you're looking for. Check out the areas we serve below.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/south-florida-pet-dental" className="btn-primary px-6 py-3">
              View Areas We Serve
            </Link>
            <Link to="/" className="btn-secondary px-6 py-3">
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const meta = generateLocationMeta(location);
  const breadcrumbs = createBreadcrumbs.location(location.name, location.slug);
  const relatedLocations = getRelatedLocations(location, locations, 4);
  
  // Select relevant FAQs for this location
  const relevantFAQs = faqItems.slice(0, 4);

  return (
    <>
      <SEO 
        meta={meta}
        schema={[
          generateLocationSchema(location),
          generateFAQSchema(relevantFAQs),
        ]}
        breadcrumbs={breadcrumbs}
      />

      {/* Hero Section */}
      <section className="pt-8 pb-12 bg-gradient-to-br from-brand-teal/10 to-white">
        <div className="container-custom">
          <Breadcrumbs items={breadcrumbs} className="mb-6" />
          
          <div className="text-center max-w-4xl mx-auto">
            <span className="inline-block bg-brand-teal/10 text-brand-teal px-4 py-1 rounded-full text-sm font-semibold mb-4">
              <i className="fas fa-map-marker-alt mr-2"></i>
              {location.county} County, {location.state}
            </span>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 font-nantes">
              Pet Dental Care for {location.name} Residents
            </h1>
            
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-4">
              Dog and cat dental care for {location.name} residents at our Fort Lauderdale clinic, 1800 E Commercial Blvd.
            </p>
            
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <BookAppointmentLink 
                className="inline-flex items-center justify-center bg-brand-teal text-white text-lg px-8 py-4 rounded-xl font-bold hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl"
              >
                <i className="fas fa-calendar-alt mr-2"></i>
                Book in Fort Lauderdale
              </BookAppointmentLink>
              <a 
                href="tel:+19544663775" 
                className="inline-flex items-center justify-center gap-2 border-2 border-brand-teal text-brand-teal text-lg px-8 py-4 rounded-xl font-bold hover:bg-brand-teal hover:text-white transition-all"
              >
                <i className="fas fa-phone"></i>
                (954) 466-3775
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why CHOMP Section */}
      <section className="bg-white py-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-primary mb-6 font-nantes">
                Why {location.name} Pet Parents Choose CHOMP
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {location.uniqueContent}
              </p>
              
              {/* Key Benefits */}
              <div className="grid sm:grid-cols-3 gap-4 mt-8">
                <div className="bg-gray-50 p-5 rounded-xl text-center">
                  <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-shield-alt text-brand-teal text-xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 mb-1">Veterinary Assessment</h4>
                  <p className="text-sm text-gray-600">Care planned for your pet</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-xl text-center">
                  <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-dollar-sign text-brand-teal text-xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 mb-1">Itemized Pricing</h4>
                  <p className="text-sm text-gray-600">Review your treatment estimate</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-xl text-center">
                  <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-award text-brand-teal text-xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 mb-1">Dental-Focused Team</h4>
                  <p className="text-sm text-gray-600">All we do is dental</p>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <ServiceLinks className="bg-gray-50 rounded-xl p-6 mb-6" />
              
              {/* Quick Contact Card */}
              <div className="bg-brand-teal text-white rounded-xl p-6">
                <h3 className="font-bold text-lg mb-3">Visit Us From {location.name}</h3>
                <div className="space-y-3 text-sm opacity-90">
                  <p>
                    <i className="fas fa-map-marker-alt mr-2"></i>
                    1800 E Commercial Blvd<br/>
                    Fort Lauderdale, FL 33308
                  </p>
                  <p>
                    <i className="fas fa-clock mr-2"></i>
                    Mon-Fri: 7am-6pm<br/>
                    Sat: 8am-2pm
                  </p>
                </div>
                <a 
                  href="https://maps.google.com/?q=1800+E+Commercial+Blvd+Fort+Lauderdale+FL" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-4 text-white font-semibold hover:underline"
                >
                  Get Directions →
                  <OpensInNewTab />
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Neighborhoods Served */}
      <section className="bg-gray-50 py-12">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-primary mb-6 text-center font-nantes">
            Neighborhoods We Serve in {location.name}
          </h2>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {location.neighborhoods.map((neighborhood, idx) => (
              <span 
                key={idx} 
                className="bg-white border border-brand-teal text-brand-teal px-4 py-2 rounded-full text-sm font-medium shadow-sm"
              >
                {neighborhood}
              </span>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-2 font-medium">ZIP Codes Served:</p>
            <p className="text-sm text-gray-500 max-w-2xl mx-auto">
              {location.zipCodes.join(' • ')}
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <Services />

      {/* Trust Section */}
      <section className="bg-brand-teal text-white py-12">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6 font-nantes">
            Trusted by {location.name} Pet Parents
          </h2>
          <p className="text-xl mb-8">Read pet owners' experiences on Google and Yelp</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="https://www.google.com/maps/place/CHOMP+Pet+Dental"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-brand-teal px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-all inline-flex items-center justify-center gap-2"
            >
              <i className="fab fa-google" aria-hidden="true"></i>
              Read Google Reviews
              <OpensInNewTab />
            </a>
            <a
              href="https://www.yelp.com/biz/chomp-pet-dental-fort-lauderdale"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white px-6 py-3 rounded-lg font-bold hover:bg-white hover:text-brand-teal transition-all inline-flex items-center justify-center gap-2"
            >
              <i className="fab fa-yelp" aria-hidden="true"></i>
              Read Yelp Reviews
              <OpensInNewTab />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-16">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-primary mb-8 text-center font-nantes">
            Pet Dental FAQs for {location.name} Residents
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {relevantFAQs.map((faq, index) => (
              <details 
                key={index} 
                className="bg-gray-50 rounded-xl p-5 group"
                open={index === 0}
              >
                <summary className="font-bold text-gray-800 cursor-pointer list-none flex items-center justify-between">
                  {faq.question}
                  <i className="fas fa-chevron-down text-brand-teal transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed whitespace-pre-line">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/#faq" className="text-brand-teal font-semibold hover:underline">
              View All FAQs →
            </Link>
          </div>
        </div>
      </section>

      {/* Related Locations */}
      {relatedLocations.length > 0 && (
        <section className="bg-gray-50 py-12">
          <div className="container-custom">
            <h2 className="text-2xl font-bold text-primary mb-8 text-center font-nantes">
              We Also Serve Nearby Cities
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {relatedLocations.map((loc) => (
                <Link
                  key={loc.slug}
                  to={`/locations/${loc.slug}`}
                  className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-all text-center group"
                >
                  <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-brand-teal/20 transition-colors">
                    <i className="fas fa-map-marker-alt text-brand-teal"></i>
                  </div>
                  <h3 className="font-bold text-gray-800 group-hover:text-brand-teal transition-colors">
                    {loc.name}
                  </h3>
                  <p className="text-sm text-gray-500">{loc.driveTime} drive</p>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/south-florida-pet-dental" className="text-brand-teal font-semibold hover:underline">
                View Areas We Serve →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Blog Posts */}
      <section className="bg-white py-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-primary mb-6 font-nantes">
              Pet Dental Tips for {location.name} Pet Owners
            </h2>
            <RelatedPosts limit={3} title="" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white py-12">
        <div className="container-custom max-w-4xl">
          <ContextualCTA location={location} variant="box" />
        </div>
      </section>

      <Footer />
    </>
  );
};

export default LocationPage;
